import config from '../../config/config';

import deleteRequest from './base/delete';
import getRequest from './base/get';
import patchRequest from './base/patch';
import postRequest from './base/post';

interface QueryParameters {
  [key: string]: any;
}

export default {
  /**
   * Fetch the Accounts list with pagination
   * @param {any} query query
   * @returns {any} promise
   */
  fetchDashboardCardDetails: (query: QueryParameters): Promise<any> => {
    return getRequest(`${config.reportApiUrl}api/super-admin/v2/dashboard/total-dashboard-cards`, {
      ...query,
    });
  },
  /**
   * Fetch the all charging station list location in map
   * @param {any} query query
   * @returns {any} promise
   */
  fetchAllChargingStationList: (query: QueryParameters): Promise<any> => {
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/charging-station/charging-stations-map`,
      { ...query },
    );
  },
  /**
   * Fetch the all charging station list location in map
   * @param {any} id id
   * @returns {any} promise
   */
  getChargingStationDetails: (id: string): Promise<any> => {
    return getRequest(
      `${config.commonApiUrl}api/super-admin/v1/charging-station/charging-stations-map/${id}`,
      {},
    );
  },
  /**
   * Fetch the all charging station list location in map
   * @param {any} type type
   * @param {any} stationId station id
   * @returns {any} promise
   */
  getRevenueAndPowerConsumedDetails: (type: string, stationId: string = ''): Promise<any> => {
    const request: any = { type };
    if (stationId) {
      request.stationId = stationId;
    }
    return getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-transaction/revenue-and-power-consumed-details`,
      request,
    );
  },
  // Cards data
  /**
   * Fetch devices API
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the connector count
   */
  getConnectorCount: (query: QueryParameters) =>
    getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-point/total-connectors`, {
      ...query,
    }),
  /**
   * Fetch devices API
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the station count
   */
  getStationCount: (query: QueryParameters) =>
    getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-station/total-charging-stations`,
      { ...query },
    ),
  /**
   * Fetch devices API
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the charging point count
   */
  getChargingPointCount: (query: QueryParameters) =>
    getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-point/total-charging-points`, {
      ...query,
    }),
  /**
   * Fetch devices API
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the defect count
   */
  getDefectCount: (query: QueryParameters) =>
    getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-point/total-faulted-connectors`, {
      ...query,
    }),
  /**
   * Fetch devices API
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the connector status report
   */
  getConnectorStatusReport: (query: QueryParameters) =>
    getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-point/connector-status-report`, {
      ...query,
    }),
  /**
   * Fetch devices API
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the device status report
   */
  getDeviceStatusReport: (query: QueryParameters) =>
    getRequest(`${config.reportApiUrl}api/super-admin/v1/charging-point/device-status-report`, {
      ...query,
    }),
  /**
   * Fetch devices API
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the alerts report
   */
  getAlertsReport: (query: QueryParameters) =>
    getRequest(`${config.reportApiUrl}api/facility/v1/charging-point/alerts-report`, { ...query }),
  /**
   * Fetch devices API
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the scheduled charging report
   */
  getScheduleChargingReport: (query: QueryParameters) =>
    getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/schedule-charging-confirmed-report`,
      {
        ...query,
      },
    ),
  /**
   * Fetch devices API
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the performance report
   */
  getStationPerformanceReport: (query: QueryParameters) =>
    getRequest(
      `${config.reportApiUrl}api/super-admin/v2/charging-point/station-performance-report`,
      {
        ...query,
      },
    ),
  /**
   * Fetch devices API
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the active/idle report
   */
  getDeviceActiveIdleReport: (query: QueryParameters) =>
    getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/device-active-idle-report`,
      {
        ...query,
      },
    ),
  /**
   * Fetch devices API
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the utilization report
   */
  getDeviceUtilizationReport: (query: QueryParameters) =>
    getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/device-utilization-report`,
      {
        ...query,
      },
    ),
  /**
   * Fetch devices API
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the power consumed report
   */
  getPowerConsumedReport: (query: QueryParameters) =>
    getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-transaction/power-consumption-report`,
      {
        ...query,
      },
    ),
  /**
   * Fetch charging status
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the charging status
   */
  fetchChargingStatus: (query: QueryParameters) =>
    getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/total-device-status-report`,
      { ...query },
    ),
  /**
   * Fetch active charging point counts
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the active charging point counts
   */
  getActiveChargingPointCounts: (query: QueryParameters) =>
    getRequest(
      `${config.reportApiUrl}api/super-admin/v1/charging-point/total-active-device-count-report`,
      { ...query },
    ),

  /**
   * Create custom dashboard
   * @param {any} data - The dashboard configuration data
   * @param {any} isFileUpload - Boolean flag if uploading a file
   * @returns {any} A promise that resolves with the creation response
   */
  createCustomDashboard: (data: any = {}, isFileUpload = false): Promise<any> =>
    postRequest(`${config.reportApiUrl}api/super-admin/v2/custom-dashboard`, data, isFileUpload),
  /**
   * Get custom dashboard list
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the list of dashboards
   */
  getCustomDashboardList: (query: QueryParameters): Promise<any> =>
    getRequest(`${config.reportApiUrl}api/super-admin/v2/custom-dashboard`, { ...query }),
  /**
   * Delete custom dashboard
   * @param {any} id - The dashboard ID to delete
   * @returns {any} A promise that resolves with the deletion response
   */
  deleteCustomDashboard: (id: string): Promise<any> =>
    deleteRequest(`${config.reportApiUrl}api/super-admin/v2/custom-dashboard/${id}`),
  /**
   * Get custom dashboard details by id
   * @param {any} id - The dashboard ID
   * @returns {any} A promise that resolves with the details response
   */
  getCustomDashboardDetails: (id: string): Promise<any> =>
    getRequest(`${config.reportApiUrl}api/super-admin/v2/custom-dashboard/${id}`, {}),
  /**
   * Update custom dashboard
   * @param {any} id - The dashboard ID to update
   * @param {any} data - The update data payload
   * @param {any} isFileUpload - Flag if the request has file upload
   * @returns {any} A promise that resolves with the update response
   */
  updateCustomDashboard: (id: string, data: any = {}, isFileUpload = false): Promise<any> =>
    patchRequest(
      `${config.reportApiUrl}api/super-admin/v2/custom-dashboard/${id}`,
      data,
      isFileUpload,
    ),
  /**
   * Get default custom dashboard
   * @returns {any} A promise that resolves with the default dashboard
   */
  getDefaultCustomDashboard: (): Promise<any> =>
    getRequest(`${config.reportApiUrl}api/super-admin/v2/custom-dashboard/default`, {}),
  /**
   * Get penalty revenue
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the penalty revenue data
   */
  getPenaltyRevenue: (query: QueryParameters): Promise<any> =>
    getRequest(`${config.reportApiUrl}api/super-admin/v2/dashboard/penalty-revenue-chart`, {
      ...query,
    }),
  /**
   * Get station health score
   * @param {any} query - The query parameters
   * @returns {any} A promise that resolves with the health score data
   */
  getStationHealthScore: (query: QueryParameters): Promise<any> =>
    getRequest(`${config.reportApiUrl}api/super-admin/v2/dashboard/station-health-score`, {
      ...query,
    }),
  /**
   * Upload the CPO profile image.
   * @param {QueryParameters} query - The FormData payload containing the profile image file
   * @returns {Promise<any>} A promise that resolves once the upload completes
   */
  uploadCPOProfileImage: (query: QueryParameters): Promise<any> =>
    patchRequest(
      `${config.subscription.accountApiUrl}api/cpo/v1/user/profile-image`,
      query,
      true,
    ),
  /**
   * Fetch the CPO user's profile details.
   * @returns {Promise<any>} A promise that resolves with the user profile details
   */
  getCPOUserProfile: (): Promise<any> =>
    getRequest(`${config.subscription.accountApiUrl}api/cpo/v1/user/profile`, {}),
  /**
   * Dummy API to fetch the saved time zone preference for the current user.
   * Replace with a real endpoint once the backend supports it.
   * @returns {Promise<{ data: { timeZone: string | null } }>} A promise that resolves with the saved time zone, or null if none is saved yet
   */
  getUserTimeZone: (): Promise<{ data: { timeZone: string | null } }> =>
    new Promise((resolve) => {
      setTimeout(() => {
        resolve({ data: { timeZone: null } });
      }, 300);
    }),
  /**
   * Dummy API to persist the selected time zone preference for the current user.
   * Replace with a real endpoint once the backend supports it.
   * @param {{ timeZone: string }} query - The time zone payload
   * @returns {Promise<{ data: { timeZone: string } }>} A promise that resolves with the saved time zone
   */
  updateUserTimeZone: (query: { timeZone: string }): Promise<{ data: { timeZone: string } }> =>
    new Promise((resolve) => {
      setTimeout(() => {
        resolve({ data: { timeZone: query.timeZone } });
      }, 300);
    }),
};
